"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.propTypes = exports.defaultProps = exports.default = exports.SelectableCard = void 0;
require("./SelectableCard.css");
var _classnames = _interopRequireDefault(require("classnames"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireDefault(require("react"));
var _Button = _interopRequireDefault(require("../Button/Button"));
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
/**
 * SelectableCard component
 *
 * @method
 * @param { SelectableCardProps } props
 * @returns SelectableCard
 */

const SelectableCard = props => {
  const {
    ariaDescribedBy,
    children,
    disabled = defaultProps.disabled,
    externalCardAction,
    inputId,
    inputName,
    inputValue,
    inputType = defaultProps.inputType,
    ref,
    selected = defaultProps.selected,
    style = defaultProps.style,
    tabIndex
  } = props;
  const componentClassName = (0, _classnames.default)('pal-c-SelectableCard', {
    'pal-c-SelectableCard--selected': !disabled && selected,
    'pal-c-SelectableCard--disabled': disabled,
    [`pal-c-SelectableCard--${style}`]: style
  });
  if (!inputType) {
    return null;
  }
  const isButton = inputType === 'button';
  return /*#__PURE__*/_react.default.createElement("div", {
    className: componentClassName
  }, isButton ? /*#__PURE__*/_react.default.createElement(_Button.default, {
    buttonRef: ref,
    variant: "unstyled",
    "aria-disabled": disabled,
    disabled: disabled,
    onClick: externalCardAction,
    tabIndex: tabIndex,
    "aria-describedby": ariaDescribedBy,
    "aria-selected": selected || undefined
  }, children) : /*#__PURE__*/_react.default.createElement("label", {
    htmlFor: inputId
  }, /*#__PURE__*/_react.default.createElement("input", {
    "aria-describedby": ariaDescribedBy,
    ref: ref,
    id: inputId,
    type: inputType,
    name: inputName,
    checked: selected,
    disabled: disabled,
    value: inputValue,
    onChange: externalCardAction,
    tabIndex: tabIndex
  }), children));
};

/**
 * Property type definitions
 * @typedef SelectableCardProps
 * @type {object}
 * @property {string} ariaDescribedBy - sets the aria-describedby attribute for button or input elements
 * @property {node} children - Children slot where card content components can be provided
 * @property {boolean} disabled - Sets the disabled state
 * @property {func} externalCardAction - Sets the onClick for button or onChange for input element.
 * @property {string} inputId - Sets the id for radio/checkbox input
 * @property {string} inputName - Sets the name for radio/checkbox input
 * @property {string} inputValue - Sets the value for radio/checkbox input
 * @property {string} inputType - Sets the type of input ( radio, checkbox or button )
 * @property {any} ref - Sets the ref of the selectable card
 * @property {boolean} selected - Sets the selected state for button/ checked state for radio/checkbox input
 * @property {string} style - Sets the style of the card ( default or grayscale )
 * @property {number} tabIndex - Sets the tabIndex for the button or input elements.
 */
exports.SelectableCard = SelectableCard;
const propTypes = exports.propTypes = {
  /**
   * Sets the aria-describedby attribute for button or input elements
   */
  ariaDescribedBy: _propTypes.default.string,
  /**
   * Children slot where card content components can be provided
   */
  children: _propTypes.default.node,
  /**
   * Sets the disabled state
   */
  disabled: _propTypes.default.bool,
  /**
   * Sets the id for radio/checkbox input
   */
  inputId: _propTypes.default.string,
  /**
   * Sets the name for radio/checkbox input
   */
  inputName: _propTypes.default.string,
  /**
   * Sets the value for radio/checkbox input
   */
  inputValue: _propTypes.default.string,
  /**
   * Sets the type of input
   */
  inputType: _propTypes.default.oneOf(['radio', 'checkbox', 'button']),
  /**
   * card reference
   */
  ref: _propTypes.default.any,
  /**
   * Sets the selected state for button/ checked state for radio/checkbox input
   */
  selected: _propTypes.default.bool,
  /**
   * Sets the style of the card
   */
  style: _propTypes.default.oneOf(['default', 'grayscale']),
  /**
   * Sets the tab-index of the button or input element
   */
  tabIndex: _propTypes.default.number
};

/**
 * Default values for passed properties
 *
 * @type {object}
 * @property {boolean} disabled='false' - Sets the default disabled state to false
 * @property {string} inputType='button' - Sets the default inputType to button
 * @property {boolean} selected='false' - Sets the default selected state to false
 * @property {string} style='default' - Sets the default style of the card
 */
const defaultProps = exports.defaultProps = {
  disabled: false,
  inputType: 'button',
  selected: false,
  style: 'default'
};
SelectableCard.propTypes = propTypes;
var _default = exports.default = SelectableCard;